import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useUserPreferredGenres } from '@/hooks/genres/useUserPreferredGenres'
import { type LibraryBook, preferredVsActual } from '@/lib/insights'
import { InsightCard, InsightEmpty } from './InsightCard'

/** What the user says they like vs what they actually read */
export function PreferredVsActualCard({ books }: { books: LibraryBook[] }) {
  const { t } = useTranslation()
  const { data: preferred = [] } = useUserPreferredGenres()

  const rows = useMemo(
    () =>
      preferredVsActual(
        books,
        preferred.map((g) => ({ id: g.id, name: g.name, color: g.color }))
      ).slice(0, 6),
    [books, preferred]
  )

  const surprise = rows.find((r) => !r.preferred && r.read > 0)
  const neglected = rows.find((r) => r.preferred && r.read === 0)

  return (
    <InsightCard
      icon="💡"
      title={t('insights.preferred.title')}
      description={t('insights.preferred.description')}
    >
      {preferred.length === 0 ? (
        <InsightEmpty>
          {t('insights.preferred.noPreferences')}{' '}
          <Link to="/dashboard/settings/preferred-genres" className="text-primary hover:underline">
            {t('insights.preferred.setPreferences')}
          </Link>
        </InsightEmpty>
      ) : (
        <div className="space-y-3">
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.genre.id} className="flex items-center gap-2 text-sm">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: r.genre.color }}
                />
                <span className="min-w-0 flex-1 truncate text-foreground">
                  {r.genre.name}
                  {r.preferred && (
                    <span className="ml-1" title={t('insights.preferred.preferredTag')}>
                      ❤️
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t('insights.preferred.readCount', { count: r.read })}
                  {r.averageRating !== null && ` · ${r.averageRating}⭐`}
                </span>
              </li>
            ))}
          </ul>
          {(surprise || neglected) && (
            <div className="space-y-1 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              {surprise && (
                <p>{t('insights.preferred.surprise', { genre: surprise.genre.name })}</p>
              )}
              {neglected && (
                <p>{t('insights.preferred.neglected', { genre: neglected.genre.name })}</p>
              )}
            </div>
          )}
        </div>
      )}
    </InsightCard>
  )
}
